
import React, { useState } from "react";
import { createPortal } from "react-dom";


// The plain "are you sure" for a removal. It is shown ONLY when the people being
// removed own no projects — when they do own something, ReassignProjectsModal
// takes its place and acts as the confirmation itself. That keeps it to one
// dialog either way, and lets the copy here state what happens as fact instead
// of promising an outcome it can't know.
const RemoveMemberModal = ({
  isOpen,
  onClose,
  onConfirm,
  member,
  members,
  // Set when the owned-projects lookup failed. A broken lookup never blocks a
  // removal, but it does mean we can't claim there is nothing to hand over.
  ownershipUnknown = false,
}) => {
  const [isRemoving, setIsRemoving] = useState(false);
  const [isRemoved, setIsRemoved] = useState(false);


  if (!isOpen) return null;

  const list = members && members.length ? members : member ? [member] : [];
  const count = list.length;
  const names = list.map((m) => m.name || m.email).filter(Boolean).join(", ");


  const handleConfirm = async () => {
    setIsRemoving(true);
    try {
      await onConfirm(list);
      setIsRemoved(true);
      
      // Show "Removed" state for 2 seconds then close
      setTimeout(() => {
        setIsRemoved(false);
        setIsRemoving(false);
        onClose();
      }, 1000);
    } catch (error) {
      setIsRemoving(false);
      // Error is already handled in parent component
      
      // Close popup after 1 second when API call fails
      setTimeout(() => {
        onClose();
      }, 1000);
    }
  };


  const getButtonContent = () => {
    if (isRemoved) {
      return "Removed";
    }
    if (isRemoving) {
      return (
        <>
          Removing
        </>
      );
    }
    return count > 1 ? "Remove Members" : "Remove Member";
  };


  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center font-outfit">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/30 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={!isRemoving ? onClose : undefined}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 animate-in fade-in zoom-in-95 duration-200">
        {/* Content */}
        <div className="p-6 text-center">
          <h2 className="text-2xl font-semibold text-[#18181B] mb-4">
            {count > 1
              ? `Remove ${count} members from the organization?`
              : "Remove this member from the organization?"}
          </h2>

          <div className="space-y-1 mb-8">
            {count > 1 && names && (
              <p className="text-[15px] font-medium !text-[#3F3F46]">{names}</p>
            )}
            <p className="text-[15px] !text-[#71717A] font-normal">
              {count > 1
                ? "Once removed, they lose access to this workspace, including projects they created."
                : "Once removed, this member loses access to this workspace, including projects they created."}
            </p>
            <p className="text-[15px] font-medium !text-[#71717A]">
              {ownershipUnknown
                ? "We couldn't check what they own. Anything left behind can be handed over later from the project's menu."
                : "They don't own any projects, so nothing needs handing over."}
            </p>
          </div>


          {/* Action Buttons */}
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={onClose}
              disabled={isRemoving}
              className="px-8 py-2.5 border border-[#C95EBE] !text-[#C95EBE] rounded-lg text-[15px] font-medium hover:bg-[#C95EBE]/5 transition-colors min-w-[140px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={isRemoving || isRemoved}
              className={`px-8 py-2.5 bg-white border rounded-lg text-[15px] font-medium transition-colors min-w-[160px] flex items-center justify-center gap-2 ${
                isRemoved 
                  ? 'border-green-500 !text-green-600 bg-green-50' 
                  : 'border-gray-300 !text-gray-700 hover:!text-white hover:bg-[#E21919BF] hover:border-[#E21919BF]'
              } disabled:cursor-not-allowed`}
            >
              {getButtonContent()}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};


export default RemoveMemberModal;
